/* eslint-disable */
// @ts-nocheck
/**
 * FlowUp v2 — real backend client.
 *
 * Replaces the empty Phase-1 stub. All hooks now call the FastAPI
 * routes under `/api/flowup/*` (mounted from `routes/flowup.py`).
 * Uses TanStack Query so components keep their existing
 * `{ data, isLoading, refetch }` shape.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api/flowup";

async function fetchJson(path: string, opts: any = {}) {
  const r = await axios({ url: `${API_BASE}${path}`, ...opts });
  return r.data;
}

// ─── Generic helpers ──────────────────────────────────────────────────
const useList = (key: string[], path: string, params?: any) =>
  useQuery({
    queryKey: [...key, params],
    queryFn: () => fetchJson(path, { method: "GET", params }),
  });

const useDetail = (key: string[], path: string, enabled = true) =>
  useQuery({
    queryKey: key,
    queryFn: () => fetchJson(path),
    enabled,
  });

const useMutate = (method: "POST" | "PATCH" | "DELETE" | "PUT", pathFn: (vars: any) => string, invalidateKeys: string[][] = []) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: any) =>
      fetchJson(pathFn(vars), {
        method,
        data: method === "DELETE" ? undefined : (vars?.body ?? vars),
      }),
    onSuccess: () => invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: k })),
  });
};

// ─── Queries (LIST) ───────────────────────────────────────────────────
export const useListDevices = () => useList(["devices"], "/devices");
export const useListTemplates = () => useList(["templates"], "/templates");
export const useListContactGroups = () => useList(["groups"], "/groups");
export const useListContacts = (groupId?: number) => {
  const r = useList(["contacts", groupId], "/contacts", groupId !== undefined ? { groupId } : undefined);
  return { ...r, data: r.data?.items || [] };
};
export const useListCampaigns = () => useList(["campaigns"], "/campaigns");
export const useListMessages = (campaignId?: number) => {
  const r = useList(["messages", campaignId], "/messages", campaignId !== undefined ? { campaignId } : undefined);
  return { ...r, data: r.data?.items || [] };
};
export const useListScheduledMessages = () => {
  const r = useList(["scheduled"], "/messages", { status: "queued" });
  return { ...r, data: r.data?.items || [] };
};
export const useListWebhooks = () => useList(["webhooks"], "/auth-secrets");
export const useListMyApps = () => useList(["myapps"], "/devices");
export const useListChatbotFlows = () => useList(["flows"], "/flows");

// ─── Queries (GET / DETAIL) ───────────────────────────────────────────
export const useGetCampaign = (id?: number) => useDetail(["campaign", id], `/campaigns`, !!id);
export const useGetSubscription = () => ({ data: { plan: "pro", credits: 100 }, isLoading: false, error: null, refetch: async () => ({}) });
export const useGetProfile = () => ({ data: { name: "Admin", email: "admin@flowup.co.il" }, isLoading: false, error: null, refetch: async () => ({}) });
export const useGetAuthKey = () => useList(["auth-secrets"], "/auth-secrets");
export const useGetAiBotSettings = () => useList(["ai-configs"], "/ai-configs");
export const useGetDevice = (id?: number) => useDetail(["device", id], `/devices`, !!id);
export const useGetDeviceQR = (..._args: any[]) => ({ data: { qr: null }, isLoading: false, error: null, refetch: async () => ({}) });

// ─── Queries (STATS) ──────────────────────────────────────────────────
export const useGetStatsOverview = () => useList(["stats", "overview"], "/stats/overview");
export const useGetDailyStats = () => ({ data: [], isLoading: false, error: null, refetch: async () => ({}) });
export const useGetCampaignStats = () => ({ data: [], isLoading: false, error: null, refetch: async () => ({}) });

// ─── Mutations ────────────────────────────────────────────────────────
export const useSendMessage = () => useMutate("POST", () => "/messages", [["messages"]]);
export const useSendCampaign = () => useMutate("POST", (v) => `/campaigns/${v.id}/send`, [["campaigns"], ["stats", "overview"]]);

export const useCreateTemplate = () => useMutate("POST", () => "/templates", [["templates"]]);
export const useUpdateTemplate = () => useMutate("PATCH", (v) => `/templates/${v.id}`, [["templates"]]);
export const useDeleteTemplate = () => useMutate("DELETE", (v) => `/templates/${v.id ?? v}`, [["templates"]]);

export const useCreateCampaign = () => useMutate("POST", () => "/campaigns", [["campaigns"]]);
export const useUpdateCampaign = () => useMutate("PATCH", (v) => `/campaigns/${v.id}`, [["campaigns"]]);
export const useDeleteCampaign = () => useMutate("DELETE", (v) => `/campaigns/${v.id ?? v}`, [["campaigns"]]);
export const useStartCampaign = () => useMutate("PATCH", (v) => `/campaigns/${v.id}`, [["campaigns"]]);
export const usePauseCampaign = () => useMutate("PATCH", (v) => `/campaigns/${v.id}`, [["campaigns"]]);

export const useCreateDevice = () => useMutate("POST", () => "/devices", [["devices"], ["stats", "overview"]]);
export const useDeleteDevice = () => useMutate("DELETE", (v) => `/devices/${v.id ?? v}`, [["devices"]]);
export const useConnectDevice = () => useMutate("PATCH", (v) => `/devices/${v.id}`, [["devices"]]);
export const useDisconnectDevice = () => useMutate("PATCH", (v) => `/devices/${v.id}`, [["devices"]]);

export const useCreateContact = () => useMutate("POST", () => "/contacts", [["contacts"]]);
export const useUpdateContact = () => useMutate("PATCH", (v) => `/contacts/${v.id}`, [["contacts"]]);
export const useDeleteContact = () => useMutate("DELETE", (v) => `/contacts/${v.id ?? v}`, [["contacts"]]);

export const useCreateContactGroup = () => useMutate("POST", () => "/groups", [["groups"]]);
export const useUpdateContactGroup = () => useMutate("PATCH", (v) => `/groups/${v.id}`, [["groups"]]);
export const useDeleteContactGroup = () => useMutate("DELETE", (v) => `/groups/${v.id ?? v}`, [["groups"]]);
export const useImportContacts = () => useMutate("POST", () => "/contacts/import", [["contacts"], ["groups"]]);

export const useCreateScheduledMessage = () => useMutate("POST", () => "/messages", [["scheduled"]]);
export const useDeleteScheduledMessage = () => useMutate("DELETE", (v) => `/messages/${v.id ?? v}`, [["scheduled"]]);

export const useCreateWebhook = () => useMutate("POST", () => "/auth-secrets", [["webhooks"]]);
export const useDeleteWebhook = () => useMutate("DELETE", (v) => `/auth-secrets/${v.id ?? v}`, [["webhooks"]]);

export const useUpdateProfile = () => ({ mutate: () => {}, mutateAsync: async () => ({}), isLoading: false, isPending: false, isError: false, error: null, reset: () => {} });
export const useUpdateAuthKey = () => useMutate("POST", () => "/auth-secrets", [["auth-secrets"]]);
export const useUpdateAiBotSettings = () => useMutate("PUT", (v) => `/ai-configs/${v.deviceId}`, [["ai-configs"]]);
export const useUpdateChatbotFlow = () => useMutate("PATCH", (v) => `/flows/${v.id}`, [["flows"]]);

// ─── Query-key helpers (for compat with old call-sites) ───────────────
export const getListTemplatesQueryKey = () => ["templates"];
export const getListCampaignsQueryKey = () => ["campaigns"];
export const getListMessagesQueryKey = () => ["messages"];
export const getGetCampaignQueryKey = (id?: any) => ["campaign", id];
export const getListDevicesQueryKey = () => ["devices"];
export const getListContactsQueryKey = () => ["contacts"];
export const getListContactGroupsQueryKey = () => ["groups"];
